/**
 * 布局配置
 * - uniform: 等分网格（cols × rows）
 * - areas: 自定义跨度；cells[i] = { column, row, columnSpan?, rowSpan?, main? }
 *   column/row 为 1-based CSS Grid 坐标
 */
export const LAYOUTS = {
  single: { mode: 'uniform', cols: 1, rows: 1, slots: 1, name: '单窗口' },
  double: { mode: 'uniform', cols: 2, rows: 1, slots: 2, name: '双窗口' },
  triple: { mode: 'uniform', cols: 3, rows: 1, slots: 3, name: '三窗口' },
  quad: { mode: 'uniform', cols: 2, rows: 2, slots: 4, name: '四窗口' },
  eight: { mode: 'uniform', cols: 4, rows: 2, slots: 8, name: '八窗口' },
  sixteen: { mode: 'uniform', cols: 4, rows: 4, slots: 16, name: '十六窗口' },
  // 手机友好：一屏内竖排/宫格小窗（PC 也可用）
  stack2: { mode: 'uniform', cols: 1, rows: 2, slots: 2, name: '竖排2窗' },
  stack3: { mode: 'uniform', cols: 1, rows: 3, slots: 3, name: '竖排3窗' },
  stack4: { mode: 'uniform', cols: 1, rows: 4, slots: 4, name: '竖排4窗' },
  grid6: { mode: 'uniform', cols: 2, rows: 3, slots: 6, name: '六窗小屏' },
  // PC 自定义：L 型一大五小
  l1_5: {
    mode: 'areas',
    name: 'L型一大五小',
    slots: 6,
    cols: 3,
    rows: 3,
    cells: [
      { column: 1, row: 1, columnSpan: 2, rowSpan: 2, main: true },
      { column: 3, row: 1 },
      { column: 3, row: 2 },
      { column: 1, row: 3 },
      { column: 2, row: 3 },
      { column: 3, row: 3 }
    ]
  },
  // 左一大 + 右三小
  focus1_3: {
    mode: 'areas',
    name: '焦点一四',
    slots: 4,
    cols: 3,
    rows: 3,
    cells: [
      { column: 1, row: 1, columnSpan: 2, rowSpan: 3, main: true },
      { column: 3, row: 1 },
      { column: 3, row: 2 },
      { column: 3, row: 3 }
    ]
  },
  // 主画面左 2×2 + 右侧/底行共 7 小
  main1_7: {
    mode: 'areas',
    name: '主次一八',
    slots: 8,
    cols: 4,
    rows: 3,
    cells: [
      { column: 1, row: 1, columnSpan: 2, rowSpan: 2, main: true },
      { column: 3, row: 1 },
      { column: 4, row: 1 },
      { column: 3, row: 2 },
      { column: 4, row: 2 },
      { column: 1, row: 3 },
      { column: 2, row: 3 },
      { column: 3, row: 3, columnSpan: 2 }
    ]
  },
  // 上一大 + 下三小
  top1_3: {
    mode: 'areas',
    name: '上下一四',
    slots: 4,
    cols: 3,
    rows: 3,
    cells: [
      { column: 1, row: 1, columnSpan: 3, rowSpan: 2, main: true },
      { column: 1, row: 3 },
      { column: 2, row: 3 },
      { column: 3, row: 3 }
    ]
  }
};

export function getLayoutSlotCount(layout) {
  if (!layout) return 4;
  if (Number.isFinite(layout.slots)) return layout.slots;
  return (layout.cols || 1) * (layout.rows || 1);
}

export function getCellGridStyle(cell) {
  if (!cell) return undefined;
  const colSpan = cell.columnSpan || 1;
  const rowSpan = cell.rowSpan || 1;
  return {
    gridColumn: `${cell.column} / span ${colSpan}`,
    gridRow: `${cell.row} / span ${rowSpan}`
  };
}

/**
 * 手机端：优先「一屏塞满小窗」（fitScreen），自定义 L 型回退为宫格小窗。
 * stack2/3/4、grid6、quad(田字) 等均按 1fr 均分视口高度，减少整页乱滚。
 */
export function getEffectiveLayout(layoutKey, isMobile) {
  const base = LAYOUTS[layoutKey] || LAYOUTS.quad;
  const slots = getLayoutSlotCount(base);
  if (!isMobile) {
    return { ...base, key: layoutKey, slots, fitScreen: false };
  }

  const mobileFitMap = {
    single: { cols: 1, rows: 1, slots: 1 },
    double: { cols: 1, rows: 2, slots: 2 },
    stack2: { cols: 1, rows: 2, slots: 2 },
    triple: { cols: 1, rows: 3, slots: 3 },
    stack3: { cols: 1, rows: 3, slots: 3 },
    stack4: { cols: 1, rows: 4, slots: 4 },
    quad: { cols: 2, rows: 2, slots: 4 },
    top1_3: { cols: 1, rows: 4, slots: 4 },
    focus1_3: { cols: 1, rows: 4, slots: 4 },
    grid6: { cols: 2, rows: 3, slots: 6 },
    l1_5: { cols: 2, rows: 3, slots: 6 },
    eight: { cols: 2, rows: 4, slots: 8 },
    main1_7: { cols: 2, rows: 4, slots: 8 },
    sixteen: { cols: 2, rows: 8, slots: 16 }
  };

  const m = mobileFitMap[layoutKey];
  if (m) {
    return {
      mode: 'uniform',
      name: base.name,
      key: layoutKey,
      slots: m.slots,
      cols: m.cols,
      rows: m.rows,
      cells: undefined,
      fitScreen: m.slots <= 8, // 超过 8 路允许继续滚动
      mobileFallback: base.mode === 'areas'
    };
  }

  const cols = slots <= 4 ? 1 : 2;
  const rows = Math.ceil(slots / cols);
  return {
    mode: 'uniform',
    name: base.name,
    key: layoutKey,
    slots,
    cols,
    rows,
    cells: undefined,
    fitScreen: slots <= 6,
    mobileFallback: true
  };
}
